import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { MutableRefObject } from 'react'
import type {
  AudioSource,
  AudioSourceHandlers,
  RecordingState,
  RealtimeToken,
} from '@soniox/client'
import type { MeetingMinutesData, MeetingModeWire, PreviousAnalysis } from '@ai-meeting/shared'
import { isFatalReadyModeVerdict, verifyReadyMeetingMode } from '@/utils/meetingMode'
import {
  acknowledgesProjectUpdate,
  isSendableProjectUpdate,
  type ProjectUpdateRequest,
} from '@/utils/projectUpdateAck'
import { applyMemoPane, isMemoPaneEvent } from '@/utils/applyMemoPane'
import { sanitizeFeatureUpdate, type RealtimeFeatures } from '@/utils/realtimeFeatures'
import { fetchSonioxToken } from '@/services/soniox'
import { getServiceStrings } from '@/services/i18n'
import { getCurrentUserId, getCurrentUserName } from '@/services/session'
import { ensureBacklogUserId } from '@/services/backlogAccount'
import {
  applyLiveTranscriptEvent,
  type LiveTranscriptEvent,
  type LiveTranscriptTurn,
} from '@/utils/liveTranscriptTurns'
import type { ScreenFrameMessage } from '@/audio/ScreenCaptureSession'
import { isCaptureLostError } from '@/audio/CaptureLostError'
import {
  normalizeSttConfigContext,
  type SttConfigContext,
} from '@/utils/sttConfigContext'
import {
  upsertAssistCard,
  type AssistAskError,
  type AssistAskInput,
  type AssistCard,
  type AssistRoute,
} from '@/pages/Meetings/components/Copilot/assist.types'
import type { AssistModel } from '@/pages/Meetings/components/Copilot/assistModel'

type ProxyRecordingState =
  | Extract<
      RecordingState,
      'idle' | 'connecting' | 'recording' | 'paused' | 'stopping' | 'stopped' | 'canceled' | 'error'
    >
  /**
   * Capture died but the socket is deliberately still open, collecting the
   * translations the server had already started. Not a Soniox state — the
   * proxy's `drain` has no equivalent there.
   */
  | 'draining'
  /**
   * The browser went offline, so the socket was dropped — but the capture, and
   * with it the archive recorder, is deliberately left running (BRAI-366).
   *
   * Distinct from `canceled`, which tears the capture down and is what unmount
   * and `pagehide` use: treating a network blip as a cancel reset the session
   * timer and let the next start wipe every audio chunk recorded so far.
   */
  | 'interrupted'

type ProxySttContext = SttConfigContext

type SttProvider = 'soniox' | 'azure' | 'google_live_translate' | 'gpt-realtime-whisper'

/**
 * Maps the internal STT provider id to the value sent to the STT proxy in its
 * `config` event. The UI, meeting record, reports, and client-side token
 * rendering all keep 'azure' as the provider id, but the proxy expects the
 * wire value 'azure_speech_recognizer' for that slot. Every other provider is
 * sent unchanged.
 */
function toProxySttProvider(provider: string): string {
  return provider === 'azure' ? 'azure_speech_recognizer' : provider
}

interface UseProxySttRecordingOptions {
  source: AudioSource
  targetLang: string
  context?: ProxySttContext | null
  meetingId?: string
  sourceLang?: string
  mode?: 'one_way' | 'two_way'
  sttProvider?: SttProvider
  /**
   * Which kind of session to declare in `config.meetingMode`, or null to omit it.
   *
   * Null is the ordinary meeting: leaving the field out keeps the config
   * identical to the one sent before the field existed, so nothing about this
   * path changes for it (BRAI-460). `internal_meeting` never reaches this hook —
   * it speaks a different protocol and has one of its own.
   */
  meetingMode?: MeetingModeWire | null
  /**
   * How long the meeting clock has been running, sent as `config.meetingElapsedMs`.
   *
   * The server anchors the meeting's timeline here, so every event it sends is already on the
   * meeting's clock and this hook shifts nothing. Read when the socket connects rather than taken
   * as a value, because the tab picker and the token fetch both sit between `start()` and that
   * moment.
   */
  getMeetingElapsedMs?: () => number
  /**
   * The server's verdict, from `ready`, on where the meeting's timeline currently is.
   *
   * `resumed` true means the server re-joined a timeline it already held: a reconnect, or a
   * shared room this machine joined late. Its record is older than this client's clock, so the
   * caller should move its own origin to `Date.now() - seedMs`. False means the server took the
   * origin from `getMeetingElapsedMs`, and the caller keeps the one it has.
   */
  onTimelineAnchor?: (info: { seedMs: number; resumed: boolean }) => void
  /**
   * The shared room this machine is joining, or null when it is not sharing
   * one (BRAI-533).
   *
   * Sent as its own `config.roomId` so the grouping is legible on the wire and
   * the proxy can read it by name. `meetingId` carries the same value when a
   * room is set — that is the field the AI service groups on today — so this
   * is deliberately redundant until the two agree on one name. Omitted from
   * the config entirely when null, which keeps an unshared meeting's config
   * byte-identical to the one sent before rooms existed.
   */
  roomId?: string | null
  /**
   * Backlog project this meeting was created against, or null when it named
   * none.
   *
   * Declared on the config so the AI service knows which project's material
   * this conversation belongs to while it is still running, rather than only
   * when the minutes are asked for afterwards. Omitted entirely when null, so
   * a meeting with no project sends the config it sent before this field
   * existed.
   */
  projectKey?: string | null
  /** Audio inputs mixed by this client; used by shared rooms to elect one recorder. */
  mixedSources?: 'tab+mic' | 'mic' | 'tab' | null
  /**
   * The reader's own meeting, which is not always the one this socket names.
   *
   * In a shared room `meetingId` is the room, so without this the server has
   * no way back to the row that belongs to this person — which is why the
   * duration webhook used to 404 for anyone in a room. The auto-join detector
   * needs it for the same reason: the row it writes `sharedRoomId` onto is
   * this one, never the room's.
   */
  ownMeetingId?: string | null
  /**
   * Let the server offer this machine a shared room when it hears another one
   * on the same call.
   *
   * Permission to be *asked*, not to be merged: nothing happens until the
   * reader presses Agree on every machine in the group. False when they have
   * turned the offer off.
   */
  autoJoin?: boolean
  /**
   * Every inbound frame, before this hook decides what to do with it.
   *
   * A tap rather than a handler: it exists so a shared room can keep its own
   * history (BRAI-539), and nothing it does may change what the recording
   * does. Anything it throws is swallowed here for that reason.
   */
  onEvent?: (event: ProxySttEvent) => void
  /**
   * Pane switches to declare on `config`. Omitted when the caller has none,
   * which keeps a config that predates the field byte-identical.
   */
  getRealtimeFeatures?: () => RealtimeFeatures
  /** `ready`, so the screen can tell an old backend (no `features`) from a real map. */
  onRealtimeFeaturesReady?: (event: { features?: unknown }) => void
  /** `features.updated` for this socket. */
  onRealtimeFeaturesUpdated?: (event: { features?: unknown }) => void
  /** `features.update.error`. Non-fatal: the socket stays open. */
  onRealtimeFeaturesError?: (code: 'bad_request' | 'not_supported') => void
  /**
   * Which assistant answers Copilot, put on `config`.
   *
   * Left off entirely for a shared room, where the server refuses to be asked
   * and the panel hides the buttons.
   */
  assistModel?: AssistModel
  /** `assist.model.status` — after `ready`, and after each accepted update. */
  onAssistModelStatus?: (event: unknown) => void
  /** `assist.model.update.error`. Non-fatal: the socket stays open. */
  onAssistModelError?: (code: 'bad_request' | 'not_supported') => void
  /**
   * Which kinds of question the Copilot answers, put on `config`.
   *
   * Left off entirely for a shared room, where the server refuses to be asked
   * and the buttons filter this client's own list and nothing more.
   */
  assistRoutes?: AssistRoute[]
  /** `assist.routes.status` — after `ready`, each accepted update, and project changes. */
  onAssistRoutesStatus?: (event: unknown) => void
  /** `assist.routes.update.error`. Non-fatal: the socket stays open. */
  onAssistRoutesError?: (code: 'bad_request' | 'not_supported') => void
}

export interface ProxySttEvent extends LiveTranscriptEvent {
  message?: string
  meeting_id?: string
  /**
   * The frame's body, shape decided by `event`.
   *
   * Deliberately `unknown` rather than a union of the shapes it can be. It used to name
   * `PreviousAnalysis`, which stopped being true when the memo was split into three frames that each
   * carry one pane — and a union listing every partial shape would have to be edited every time a
   * service that is not ours adds a field. The readers narrow it: `applyMemoPane` for the memo
   * frames, an explicit cast for meeting minutes.
   */
  data?: unknown
  code?: string
  /** Sent beside `code` on an assist refusal — which of several same-`code` refusals this is. */
  reason?: string
  realtime_context_chars?: number
  /**
   * Pane switches on `ready` and `features.updated`.
   *
   * Missing on `ready` means this backend does not have the switches.
   */
  features?: unknown
  /** Sent on `ready` — which STT provider the backend actually selected for this session. */
  sttProvider?: string
  /** Sent on `ready` — the session kind the server resolved, echoing `config.meetingMode`. */
  meetingMode?: unknown
  /** Sent on `ready` — how far into the meeting the server's timeline currently is. */
  timeline_seed_ms?: number
  /** Sent on `ready` — true when the server re-joined an existing timeline rather than setting one. */
  timeline_resumed?: boolean
  /** Sent on `cost.summary` — 3-letter currency code for `total_cost_usd`. */
  currency?: string
  /** Sent on `cost.summary` — total STT/translation cost accrued for this session, in USD. */
  total_cost_usd?: number
  /** Sent on `image.processed` — slide dedup outcome. `duplicate` is expected, not an error. */
  decision?: 'new_screen' | 'duplicate' | 'error'
}

/**
 * Payload of the server's `language.updated` acknowledgment.
 *
 * Kept separate from `ProxySttEvent` because its `translation` object would
 * collide with `LiveTranscriptEvent.translation`, which is an aggregate
 * translation string.
 */
interface LanguageUpdatedEvent {
  event: 'language.updated'
  meetingId?: string
  translation?: {
    type?: string
    sourceLang?: string
    targetLang?: string
  }
  changed?: boolean
  rotating?: boolean
}

/**
 * Payload of the server's `project.updated` acknowledgment (BRAI-689).
 *
 * Kept out of `ProxySttEvent` for the same reason as the one above: both of
 * its fields carry the *applied* value, and `projectKey` on a transcript frame
 * is the one the session opened with rather than the one just accepted.
 */
interface ProjectUpdatedEvent {
  event: 'project.updated'
  meetingId?: string
  projectKey?: string | null
  userIdBacklog?: number | null
  /** False when the values asked for were already the ones in force. */
  changed?: boolean
  /** True when an STT rotation follows, arriving as `session.rotated`. */
  rotating?: boolean
}

export interface ProxyRealtimeToken extends RealtimeToken {
  translation_latency_ms?: number
  /**
   * Wall-clock time the event carrying this token reached the client.
   *
   * A second-choice clock, and only ever compared with another `received_ms`.
   * `start_ms` says when the words were spoken and is the better answer, but
   * `translation.final` — the event the Output pane is built from — carries no
   * `start_time`, so grouping the pane's newest utterance had nothing to
   * measure with (see `buildTokenRows`).
   */
  received_ms?: number
}
const STOP_FLUSH_TIMEOUT_MS = 60_000
/**
 * How long the socket is held open after `drain` before it is closed anyway.
 *
 * The server sends no acknowledgement — it finishes its final flush, keeps
 * emitting `translation.batch` on the still-open socket, then closes, so
 * `onclose` IS the completion signal and this is only the backstop for a server
 * that never gets there. Sits above the server's own budget (45s enhanced drain
 * plus 20s final flush) with room to spare; a long meeting with many queued
 * batches can still exceed it, which is a known limit rather than a guarantee.
 */
const DRAIN_TIMEOUT_MS = 90_000
// End-of-meeting minutes generation runs a heavier backend analysis pass than
// the periodic live `summarize.*` push, so it gets the same generous ceiling
// as the stop-flush wait rather than a shorter, speculative timeout.
const MEETING_MINUTES_TIMEOUT_MS = 60_000
const ASSIST_ASK_TIMEOUT_MS = 10_000

/** Live-summary push status, driven entirely by `summarize.*` server events. */
export type LiveMemoStatus = 'idle' | 'loading' | 'success' | 'error'

/** Status of the last `context.update` sent, driven by `context.updated`/`context.update.error`. */
export type ContextUpdateStatus = 'idle' | 'pending' | 'success' | 'error'

/** Status of the last `main-content.update` sent over the active STT socket. */
export type MainContentUpdateStatus = 'idle' | 'pending' | 'success' | 'error'

/** Status of the latest live translation-language update. */
export type LanguageUpdateStatus = 'idle' | 'pending' | 'success' | 'error'

/**
 * Status of the latest live `project.update` (BRAI-689).
 *
 * `unsupported` is its own answer rather than an error. A server that predates
 * the frame ignores it in silence, so nothing comes back at all — which is not
 * a failure the reader can retry, and saying "that did not work" about it
 * would be describing something that never happened.
 */
export type ProjectUpdateStatus = 'idle' | 'pending' | 'success' | 'error' | 'unsupported'

/**
 * How long to wait for `project.updated` before calling the frame unsupported.
 *
 * The server reads the database before it acknowledges and is allowed a couple
 * of seconds over it, so this sits well clear of a merely slow success.
 */
export const PROJECT_UPDATE_ACK_TIMEOUT_MS = 5000

/** Stops a pending-acknowledgment clock from whichever path settles it first. */
function clearAckTimer(ref: MutableRefObject<number | null>): void {
  if (ref.current === null) return
  window.clearTimeout(ref.current)
  ref.current = null
}

/** Total session cost pushed once by the server's `cost.summary` event at meeting end. */
export interface CostSummary {
  currency: string
  totalCostUsd: number
}

export interface ProxyRecordingReturn {
  state: ProxyRecordingState
  isActive: boolean
  isRecording: boolean
  isPaused: boolean
  error: Error | null
  /** Which STT provider the backend selected for this session (from the `ready` event's
   * `sttProvider`), e.g. "soniox" | "azure". `null` until the session reaches `ready`.
   * Named `confirmedSttProvider` (not `sttProvider`) to stay distinct from the
   * caller-supplied *requested* provider option some callers pass into this hook. */
  confirmedSttProvider: string | null
  finalTokens: RealtimeToken[]
  partialTokens: RealtimeToken[]
  /**
   * Translation tokens sourced from `translation.batch` events for a
   * Soniox session running `mode: 'both'` — a second, independent
   * translation stream kept separate from `finalTokens` so it never mixes
   * with the realtime `translation.partial`/`translation.final` stream.
   * Always empty for `azure` sessions, which keep using `finalTokens` as
   * before (Azure has no realtime translation to conflict with).
   */
  batchTokens: RealtimeToken[]
  /**
   * Source-aligned conversation turns for the live bilingual timeline.
   * `translation.final.source_segment_id` and
   * `translation.batch.source_segment_ids` enrich the same source turn even
   * when the three event streams arrive out of order.
   */
  liveTurns: LiveTranscriptTurn[]
  memoStatus: LiveMemoStatus
  memo: PreviousAnalysis | null
  memoError: string | null
  assistCards: AssistCard[]
  assistAskError: AssistAskError | null
  assistAskPending: boolean
  contextUpdateStatus: ContextUpdateStatus
  contextCharCount: number | null
  contextUpdateError: string | null
  mainContentUpdateStatus: MainContentUpdateStatus
  mainContentUpdateError: string | null
  languageUpdateStatus: LanguageUpdateStatus
  languageUpdateError: string | null
  projectUpdateStatus: ProjectUpdateStatus
  projectUpdateError: string | null
  projectUpdateErrorCode: string | null
  appliedTargetLang: string | null
  /**
   * Ref (not state) holding the latest `cost.summary` payload. `cost.summary`
   * typically arrives during the `stop` sequence kicked off by
   * `finalizeMeetingMinutes`, which callers usually invoke from a background
   * task *after* this hook's owning component has already navigated away and
   * unmounted — at which point further `setState` calls here would be
   * dropped. Reading `.current` off this stable ref instead lets a caller
   * that captured the ref object before unmount still observe the value
   * whenever it arrives.
   */
  costSummaryRef: MutableRefObject<CostSummary | null>
  start: () => void
  stop: () => Promise<void>
  finalizeMeetingMinutes: (onProcessing?: () => void) => Promise<MeetingMinutesData>
  cancel: () => void
  /**
   * Clears final/partial/batch tokens and live turns without touching the socket,
   * capture, memo, or session state. Callers that archive transcript before an
   * async re-prepare (e.g. share-tab picker) must invoke this immediately after
   * archiving so Output does not render archived + active copies together.
   */
  clearTranscriptBuffers: () => void
  /**
   * Moves every line this hook is holding onto another clock.
   *
   * A shared room has one origin — the machine it is named after — and a
   * machine merged into one mid-meeting has been stamping its lines against
   * its own. `ready` hands over the room's, and this is what brings the lines
   * already captured along with it.
   *
   * @param shiftMs How much later the room's clock reads than this one's.
   */
  rebaseTranscriptClock: (shiftMs: number) => void
  /** Drops the socket on network loss, keeping the capture and archive recording alive. */
  dropSocketForOffline: () => void
  /** Reopens the socket for an `interrupted` session, continuing the same capture. */
  reconnect: () => void
  /** Answers a shared-room offer on the open socket. False when there is none. */
  sendSharedRoomReply: (matchId: string, accept: boolean) => boolean
  /** Asks the AI service to put this machine back into a room it left. */
  sendSharedRoomRejoin: (roomId: string) => boolean
  /** Drops a rejoin frame that was queued but should not be sent on the next connect. */
  clearPendingSharedRoomRejoin: () => void
  /**
   * Moves this session onto `roomId`, keeping the capture and everything
   * already transcribed. False when there is no socket to move.
   */
  rejoinSharedRoom: (roomId: string, options?: { autoJoin?: boolean }) => boolean
  pause: () => void
  resume: () => void
  sendContextUpdate: (context: string) => void
  sendMainContentUpdate: (paragraphs: string[]) => boolean
  sendLanguageUpdate: (nextTargetLang: string) => boolean
  /**
   * Applies a project or a Backlog account to the running session (BRAI-689).
   * Returns false when nothing was sent.
   */
  sendProjectUpdate: (request: ProjectUpdateRequest) => boolean
  /** Sends `{ event: "image", image }` on the open STT socket after `ready`. Returns false when gated. */
  sendScreenFrame: (message: ScreenFrameMessage) => boolean
  sendTranslationHard: () => boolean
  sendAssistAsk: (input: AssistAskInput) => boolean
  /**
   * Turn realtime panes on or off for this socket.
   *
   * @returns False when the socket is not ready or the partial has no boolean flag.
   */
  sendFeaturesUpdate: (features: Partial<RealtimeFeatures>) => boolean
  sendAssistModelUpdate: (model: AssistModel) => boolean
  sendAssistRoutesUpdate: (routes: AssistRoute[]) => boolean
}

/** Converts proxy event seconds into Soniox-compatible token milliseconds. */
function secondsToMs(value: number | null | undefined): number | undefined {
  return typeof value === 'number' ? Math.round(value * 1000) : undefined
}

/** Normalizes numeric and string speaker IDs into the renderer's string format. */
function normalizeSpeaker(speaker: string | number | undefined): string | undefined {
  if (speaker === undefined || speaker === null || speaker === '') return undefined
  return String(speaker)
}

/** Creates a renderer-compatible realtime token from one proxy transcript event. */
function createToken({
  text,
  isFinal,
  translationStatus,
  speaker,
  language,
  startTime,
  translationLatencyMs,
}: {
  text: string
  isFinal: boolean
  translationStatus: RealtimeToken['translation_status']
  speaker?: string | number
  language?: string
  startTime?: number | null
  translationLatencyMs?: number
}): ProxyRealtimeToken {
  return {
    text,
    confidence: 1,
    is_final: isFinal,
    translation_status: translationStatus,
    speaker: normalizeSpeaker(speaker),
    language,
    start_ms: secondsToMs(startTime),
    translation_latency_ms: translationLatencyMs,
    // Stamped here, in the socket's message handler, so it records when the
    // event landed and never moves afterwards.
    received_ms: Date.now(),
  }
}
/** Appends a WebSocket token query param without clobbering existing proxy query params. */
function buildTokenizedWsUrl(wsUrl: string, token: string): string {
  const url = new URL(wsUrl)
  url.searchParams.set('token', token)
  return url.toString()
}

/** Splits structured translation turns from the proxy into renderer tokens. */
function createTranslationTurnTokens(event: ProxySttEvent): RealtimeToken[] {
  // Each `translation.final` event already carries the complete, fully-spaced
  // translation for one segment in `event.text` (with a leading space). The
  // `event.tokens[]` array is sub-word granular (per-syllable confidence
  // markers) — concatenating those loses word boundaries (e.g. "h"+"ors"+"e."
  // → "horse." only when joined correctly, but proxy emits some pieces as
  // single chars). So use `event.text` as the authoritative segment text.
  const fullText = event.text
  if (fullText && fullText.trim()) {
    const turns = Array.isArray(event.tokens) ? event.tokens : []
    // `?? undefined`: the service sends `speaker: null` for an unattributed
    // turn, which every consumer treats the same as an absent speaker.
    const speaker = turns[0]?.speaker ?? event.speaker ?? undefined
    const language = turns[0]?.language ?? event.language ?? event.target_lang ?? event.targetLang
    return [
      createToken({
        text: fullText,
        isFinal: true,
        translationStatus: 'translation',
        speaker,
        language,
        startTime: event.start_time,
        translationLatencyMs: event.translation_latency_ms,
      }),
    ]
  }

  // Fallback for legacy events that only carry `event.translation` (the
  // earlier aggregate field). Keeps backward compatibility.
  const aggregate = event.translation?.trim()
  if (aggregate) {
    return [
      createToken({
        text: aggregate,
        isFinal: true,
        translationStatus: 'translation',
        speaker: event.speaker ?? undefined,
        language: event.language ?? event.target_lang ?? event.targetLang,
        startTime: event.start_time,
        translationLatencyMs: event.translation_latency_ms,
      }),
    ]
  }

  return []
}

/** Splits `translation.batch` turns into renderer tokens — one token per speaker
 * turn in the batch, same rendering path as `translation.final`. Azure always sends
 * this event (it has no inline realtime translation like Soniox), so this case is
 * required for Azure translations to render at all. */
function createTranslationBatchTokens(event: ProxySttEvent): RealtimeToken[] {
  const turns = Array.isArray(event.translation_turns) ? event.translation_turns : []
  const turnTokens = turns
    .filter((turn) => turn.text?.trim())
    .map((turn) =>
      createToken({
        text: turn.text ?? '',
        isFinal: true,
        translationStatus: 'translation',
        speaker: turn.speaker ?? event.speaker ?? undefined,
        language: event.target_lang ?? event.targetLang,
        startTime: event.start_time,
        translationLatencyMs: event.translation_latency_ms,
      })
    )
  if (turnTokens.length > 0) return turnTokens

  // Fallback: no translation_turns[] (older backend, or a batch that failed to
  // split into turns) — use the aggregate `translation` string as one segment.
  const aggregate = event.translation?.trim()
  if (aggregate) {
    return [
      createToken({
        text: aggregate,
        isFinal: true,
        translationStatus: 'translation',
        speaker: event.speaker ?? undefined,
        language: event.target_lang ?? event.targetLang,
        startTime: event.start_time,
        translationLatencyMs: event.translation_latency_ms,
      }),
    ]
  }

  return []
}

/** Maps proxy WebSocket events into source and translation token streams. */
export function proxyEventToTokens(event: ProxySttEvent): {
  finalTokens: RealtimeToken[]
  partialTokens: RealtimeToken[]
  batchTokens: RealtimeToken[]
  clearPartial?: boolean
  /**
   * Which `translation_status` slot `clearPartial`/`partialTokens` applies to.
   * `google` (live-translate) mode runs `transcript.partial` ('original') and
   * `translation.partial` ('translation') concurrently off the same audio
   * stream, so the caller needs this to update each status's interim draft
   * independently instead of clobbering one with the other.
   */
  partialStatus?: RealtimeToken['translation_status']
} {
  switch (event.event) {
    case 'transcript.partial':
      return {
        finalTokens: [],
        partialStatus: 'original',
        batchTokens: [],
        clearPartial: !event.text,
        partialTokens: event.text
          ? [
              createToken({
                text: event.text,
                isFinal: false,
                translationStatus: 'original',
                speaker: event.speaker ?? undefined,
                language:
                  event.tokens?.[0]?.language ??
                  event.language ??
                  event.source_lang ??
                  event.sourceLang,
                startTime: event.start_time,
              }),
            ]
          : [],
      }

    case 'transcript.final':
      return {
        clearPartial: true,
        partialStatus: 'original',
        partialTokens: [],
        batchTokens: [],
        finalTokens: event.text
          ? [
              createToken({
                text: event.text,
                isFinal: true,
                translationStatus: 'original',
                speaker: event.speaker ?? undefined,
                language:
                  event.tokens?.[0]?.language ??
                  event.language ??
                  event.source_lang ??
                  event.sourceLang,
                startTime: event.start_time,
              }),
            ]
          : [],
      }

    case 'translation.partial':
      return {
        finalTokens: [],
        batchTokens: [],
        partialStatus: 'translation',
        clearPartial: !event.text,
        partialTokens: event.text
          ? [
              createToken({
                text: event.text,
                isFinal: false,
                translationStatus: 'translation',
                speaker: event.speaker ?? undefined,
                language:
                  event.tokens?.[0]?.language ??
                  event.language ??
                  event.target_lang ??
                  event.targetLang,
                startTime: event.start_time,
              }),
            ]
          : [],
      }

    case 'translation.final':
      return {
        clearPartial: true,
        partialStatus: 'translation',
        partialTokens: [],
        batchTokens: [],
        finalTokens: createTranslationTurnTokens(event),
      }

    case 'translation.batch': {
      const tokens = createTranslationBatchTokens(event)
      // `translation.batch` never feeds the realtime translation stream
      // (`finalTokens`). It is routed into the separate `batchTokens` stream for
      // EVERY provider, so it stays out of the realtime Output pane — the
      // batch turns are surfaced live through the Enhanced mode timeline
      // (`liveTurns`, built independently in the socket handler) and persisted
      // from there. Batch-only providers (azure/azure_speech_recognizer, gpt-realtime-whisper,
      // google_live_translate) therefore show only the source transcript in the
      // realtime view; inline translators (Soniox `mode: 'both'`) keep their
      // `translation.final` output in `finalTokens`, unmixed with the batch pass.
      return { partialTokens: [], batchTokens: tokens, finalTokens: [] }
    }

    default:
      return { finalTokens: [], partialTokens: [], batchTokens: [] }
  }
}

/**
 * Maps a documented STT proxy WebSocket close code to a user-facing message.
 * Returns `undefined` for codes without a documented, more specific meaning
 * (e.g. clean close, or an unrecognized code) so callers can fall back to a
 * generic error message.
 */
function getCloseCodeErrorMessage(code: number): string | undefined {
  const strings = getServiceStrings()
  switch (code) {
    case 4001:
      return strings.sttInvalidToken
    case 4002:
      return strings.sttTokenExpired
    case 4003:
      return strings.sttProviderUnavailable
    case 4004:
      return strings.sttInactivityTimeout
    case 4005:
      return strings.sttProtocolViolation
    case 1011:
      return strings.sttInternalServerError
    default:
      return undefined
  }
}

/** Resolves once a WebSocket opens or rejects on the initial connection error. */
function waitForSocketOpen(ws: WebSocket): Promise<void> {
  return new Promise((resolve, reject) => {
    ws.addEventListener('open', () => resolve(), { once: true })
    ws.addEventListener('error', () => reject(new Error('WebSocket error')), { once: true })
  })
}

/** True when the socket is open and the service has accepted config (`ready`). */
function isLiveSttReady(ws: WebSocket | null, ready: boolean): ws is WebSocket {
  return ws !== null && ws.readyState === WebSocket.OPEN && ready
}

/** Runs direct-to-proxy STT recording and exposes a Soniox-like recording snapshot. */
export function useProxySttRecording({
  source,
  targetLang,
  context,
  meetingId,
  sourceLang,
  mode,
  sttProvider,
  meetingMode = null,
  getMeetingElapsedMs,
  onTimelineAnchor,
  roomId = null,
  projectKey = null,
  mixedSources = null,
  ownMeetingId = null,
  autoJoin = false,
  onEvent,
  getRealtimeFeatures,
  onRealtimeFeaturesReady,
  onRealtimeFeaturesUpdated,
  onRealtimeFeaturesError,
  assistModel,
  onAssistModelStatus,
  onAssistModelError,
  assistRoutes,
  onAssistRoutesStatus,
  onAssistRoutesError,
}: UseProxySttRecordingOptions): ProxyRecordingReturn {
  // Held in a ref so a new tap never reopens the socket.
  const onEventRef = useRef(onEvent)
  onEventRef.current = onEvent
  const [state, setState] = useState<ProxyRecordingState>('idle')
  const [error, setError] = useState<Error | null>(null)
  const [confirmedSttProvider, setConfirmedSttProvider] = useState<string | null>(null)
  const [finalTokens, setFinalTokens] = useState<RealtimeToken[]>([])
  const [partialTokens, setPartialTokens] = useState<RealtimeToken[]>([])
  const [batchTokens, setBatchTokens] = useState<RealtimeToken[]>([])
  const [liveTurns, setLiveTurns] = useState<LiveTranscriptTurn[]>([])
  const [memoStatus, setMemoStatus] = useState<LiveMemoStatus>('idle')
  const [memo, setMemo] = useState<PreviousAnalysis | null>(null)
  const [memoError, setMemoError] = useState<string | null>(null)
  const [assistCards, setAssistCards] = useState<AssistCard[]>([])
  const [assistAskError, setAssistAskError] = useState<AssistAskError | null>(null)
  const [assistAskPending, setAssistAskPending] = useState(false)
  const assistAskTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [contextUpdateStatus, setContextUpdateStatus] = useState<ContextUpdateStatus>('idle')
  const [contextCharCount, setContextCharCount] = useState<number | null>(null)
  const [contextUpdateError, setContextUpdateError] = useState<string | null>(null)
  const [mainContentUpdateStatus, setMainContentUpdateStatus] =
    useState<MainContentUpdateStatus>('idle')
  const [mainContentUpdateError, setMainContentUpdateError] = useState<string | null>(null)
  const [languageUpdateStatus, setLanguageUpdateStatus] = useState<LanguageUpdateStatus>('idle')
  const [languageUpdateError, setLanguageUpdateError] = useState<string | null>(null)
  const [projectUpdateStatus, setProjectUpdateStatus] = useState<ProjectUpdateStatus>('idle')
  const [projectUpdateError, setProjectUpdateError] = useState<string | null>(null)
  const [projectUpdateErrorCode, setProjectUpdateErrorCode] = useState<string | null>(null)
  /** The update still waiting for its acknowledgment, or null when none is. */
  const projectUpdateRef = useRef<ProjectUpdateRequest | null>(null)
  const projectUpdateTimerRef = useRef<number | null>(null)
  const [appliedTargetLang, setAppliedTargetLang] = useState<string | null>(null)
  const costSummaryRef = useRef<CostSummary | null>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const drainTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const stopResolverRef = useRef<(() => void) | null>(null)
  const pendingMinutesRef = useRef<{
    resolve: (data: MeetingMinutesData) => void
    reject: (error: Error) => void
    onProcessing?: () => void
  } | null>(null)
  const pendingMainContentRef = useRef<string[] | null>(null)
  const targetLangRef = useRef(targetLang)
  const contextRef = useRef(context)
  /**
   * Set by `reconnect()` for the one `start()` that follows it.
   *
   * Tells that start to re-tap the capture that is still running rather than
   * building a new one, and to leave the transcript in place — the whole point
   * of an interrupted session is that nothing recorded so far is thrown away.
   */
  const resumeExistingCaptureRef = useRef(false)
  /**
   * Carries a pause across a reconnect this hook started itself.
   *
   * `start()` clears `pausedRef`, so without somewhere to put it the pause is
   * simply gone when the new socket comes up — and the reader finds the
   * meeting running, and paying for audio, because somebody else merged a
   * room.
   */
  const pauseOnReconnectRef = useRef(false)
  const sourceRef = useRef(source)
  const meetingIdRef = useRef(meetingId)
  const sourceLangRef = useRef(sourceLang)
  const modeRef = useRef(mode)
  const sttProviderRef = useRef(sttProvider)
  const meetingModeRef = useRef(meetingMode)
  const getMeetingElapsedMsRef = useRef(getMeetingElapsedMs)
  const onTimelineAnchorRef = useRef(onTimelineAnchor)
  const roomIdRef = useRef(roomId)
  const projectKeyRef = useRef(projectKey)
  const mixedSourcesRef = useRef(mixedSources)
  const ownMeetingIdRef = useRef(ownMeetingId)
  /**
   * The room this session was moved onto, until the props catch up.
   *
   * `meetingIdRef` is reassigned from props on every render, so writing the
   * new room straight into it is undone by the re-render `start()` causes —
   * and the config frame, which is sent later when the socket opens, would
   * name the room the reader just left. The store does catch up (the move
   * sets `sharedRoomId` on the meeting), and this clears itself when it does.
   */
  const rejoinedRoomIdRef = useRef<string | null>(null)
  /** One-shot override for the next config frame after `rejoinSharedRoom`. */
  const pendingConnectAutoJoinRef = useRef<boolean | null>(null)
  /** Rejoin frame to send once the socket opens after `rejoinSharedRoom`. */
  const pendingSharedRoomRejoinRef = useRef<string | null>(null)
  const autoJoinRef = useRef(autoJoin)
  const getRealtimeFeaturesRef = useRef(getRealtimeFeatures)
  const onRealtimeFeaturesReadyRef = useRef(onRealtimeFeaturesReady)
  const assistRoutesRef = useRef(assistRoutes)
  const onAssistRoutesStatusRef = useRef(onAssistRoutesStatus)
  const onAssistRoutesErrorRef = useRef(onAssistRoutesError)
  const assistModelRef = useRef(assistModel)
  const onAssistModelStatusRef = useRef(onAssistModelStatus)
  const onAssistModelErrorRef = useRef(onAssistModelError)
  const onRealtimeFeaturesUpdatedRef = useRef(onRealtimeFeaturesUpdated)
  const onRealtimeFeaturesErrorRef = useRef(onRealtimeFeaturesError)
  // Gates `onData` synchronously. `state` (and a `stateRef` mirror of it) only
  // updates on the next render, so a few audio chunks would still reach the
  // socket in the gap between clicking Pause and that render committing —
  // this ref is set the instant `pause()`/`resume()` run instead.
  const pausedRef = useRef(false)
  // Image frames must wait for `ready`; sending earlier is silently dropped by the proxy.
  const readyRef = useRef(false)

  getMeetingElapsedMsRef.current = getMeetingElapsedMs
  onTimelineAnchorRef.current = onTimelineAnchor
  roomIdRef.current = roomId
  projectKeyRef.current = projectKey
  mixedSourcesRef.current = mixedSources
  ownMeetingIdRef.current = ownMeetingId
  autoJoinRef.current = autoJoin
  targetLangRef.current = targetLang
  contextRef.current = context
  sourceRef.current = source
  if (rejoinedRoomIdRef.current !== null && rejoinedRoomIdRef.current === meetingId) {
    rejoinedRoomIdRef.current = null
  }
  meetingIdRef.current = rejoinedRoomIdRef.current ?? meetingId
  sourceLangRef.current = sourceLang
  modeRef.current = mode
  sttProviderRef.current = sttProvider
  meetingModeRef.current = meetingMode
  getRealtimeFeaturesRef.current = getRealtimeFeatures
  onRealtimeFeaturesReadyRef.current = onRealtimeFeaturesReady
  assistRoutesRef.current = assistRoutes
  onAssistRoutesStatusRef.current = onAssistRoutesStatus
  onAssistRoutesErrorRef.current = onAssistRoutesError
  assistModelRef.current = assistModel
  onAssistModelStatusRef.current = onAssistModelStatus
  onAssistModelErrorRef.current = onAssistModelError
  onRealtimeFeaturesUpdatedRef.current = onRealtimeFeaturesUpdated
  onRealtimeFeaturesErrorRef.current = onRealtimeFeaturesError

  /** Stops local capture and resolves any pending graceful stop waiter. */
  const clearDrainTimer = useCallback(() => {
    if (drainTimerRef.current !== null) {
      clearTimeout(drainTimerRef.current)
      drainTimerRef.current = null
    }
  }, [])

  /** Clears in-flight Copilot ask timers/state when the socket is gone. */
  const resetAssistAskLifecycle = useCallback(() => {
    if (assistAskTimeoutRef.current) {
      clearTimeout(assistAskTimeoutRef.current)
      assistAskTimeoutRef.current = null
    }
    setAssistAskPending(false)
    setAssistAskError(null)
    setAssistCards((current) =>
      current.map((card) =>
        card.type === 'answer' && card.status === 'pending'
          ? {
              ...card,
              status: 'failed',
              error: { code: 'session_interrupted', message: card.error?.message ?? '' },
            }
          : card
      )
    )
  }, [])

  const cleanup = useCallback(() => {
    clearDrainTimer()
    resetAssistAskLifecycle()
    try {
      sourceRef.current.stop()
    } catch {
      /* ignore teardown errors */
    }
    pausedRef.current = false
    readyRef.current = false
    stopResolverRef.current?.()
    stopResolverRef.current = null
    pendingMinutesRef.current?.reject(new Error(getServiceStrings().failedStopRecording))
    pendingMinutesRef.current = null
    pendingSharedRoomRejoinRef.current = null
    wsRef.current = null
  }, [clearDrainTimer, resetAssistAskLifecycle])

  /**
   * Drops the socket on network loss while KEEPING the capture alive.
   *
   * Deliberately not `cancel()`: that stops the audio source, which stops the
   * mixed stream, which ends the `MediaRecorder` feeding the meeting's archive —
   * and a later `start()` then cleared every chunk recorded before the outage
   * (BRAI-366). Pausing the source instead keeps the AudioContext and the
   * recorder alive, so the meeting stays one continuous recording.
   *
   * Also gates PCM the way `pause` does, so nothing is queued against a socket
   * that is already gone.
   */
  const dropSocketForOffline = useCallback(() => {
    const ws = wsRef.current
    if (!ws) return
    pausedRef.current = true
    sourceRef.current.pause?.()
    clearDrainTimer()
    resetAssistAskLifecycle()
    stopResolverRef.current?.()
    stopResolverRef.current = null
    wsRef.current = null
    // Detached before closing: `onclose` runs `cleanup()`, which stops the audio
    // source — the very thing this function exists to keep alive. Everything
    // else `cleanup` would have done is handled above.
    ws.onclose = null
    ws.close()
    setState('interrupted')
  }, [clearDrainTimer, resetAssistAskLifecycle])

  /** Cancels the active WebSocket session without waiting for final proxy events. */
  const cancel = useCallback(() => {
    const ws = wsRef.current
    setState('canceled')
    ws?.close()
    cleanup()
  }, [cleanup])

  /**
   * Drops the socket and the capture when the page this hook belongs to goes
   * away, so a meeting nobody ended stops costing money.
   *
   * There was no teardown at all before: navigating to another screen kept the
   * proxy socket open and the microphone/tab capture running, and the AI service
   * kept billing for it, until the tab itself was closed. Held in a ref so the
   * effect can depend on nothing and fire only on unmount — depending on
   * `cancel` would tear the recording down every time its identity changed.
   *
   * `pagehide` covers the reload/close half, which unmount does not run for.
   */
  const cancelRef = useRef(cancel)
  cancelRef.current = cancel
  useEffect(() => {
    const abandon = () => cancelRef.current()
    window.addEventListener('pagehide', abandon)
    return () => {
      window.removeEventListener('pagehide', abandon)
      abandon()
    }
  }, [])

  /** Waits for the socket to close, forcing it closed after a bounded timeout. */
  const waitForSocketClose = useCallback((ws: WebSocket, timeoutMs: number): Promise<void> => {
    return new Promise<void>((resolve) => {
      stopResolverRef.current = resolve
      window.setTimeout(() => {
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
          ws.close(1000)
        }
        resolve()
      }, timeoutMs)
    })
  }, [])

  /** Gracefully stops capture, asks the proxy to flush, and waits for socket close. */
  const stop = useCallback(async () => {
    const ws = wsRef.current
    if (!ws) {
      cleanup()
      setState('stopped')
      return
    }

    setState('stopping')
    try {
      sourceRef.current.stop()
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ event: 'stop' }))
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error(getServiceStrings().failedStopRecording))
    }

    await waitForSocketClose(ws, STOP_FLUSH_TIMEOUT_MS)
  }, [cleanup, waitForSocketClose])

  /**
   * Requests the on-demand end-of-meeting minutes over the same STT socket:
   * stops local audio capture, sends `{ event: 'meeting-minutes' }`, invokes
   * `onProcessing` the moment the server acknowledges with
   * `meeting-minutes.processing` (fire-and-forget checkpoint for callers that
   * only need to know the request is in flight), then resolves with the
   * structured `MeetingMinutesData` once `meeting-minutes.result` arrives —
   * asking the proxy to `stop` and close right after. Rejects (and still
   * requests a `stop`/close) on `meeting-minutes.error`, an unexpected socket
   * close, or the bounded `MEETING_MINUTES_TIMEOUT_MS` ceiling.
   *
   * @param onProcessing Optional callback fired once on `meeting-minutes.processing`.
   * @returns The structured meeting minutes payload from `meeting-minutes.result`.
   * @throws When the socket isn't open, the server reports `meeting-minutes.error`,
   *   the socket closes before a response arrives, or the request times out.
   */
  const finalizeMeetingMinutes = useCallback(
    async (onProcessing?: () => void): Promise<MeetingMinutesData> => {
      const ws = wsRef.current
      try {
        sourceRef.current.stop()
      } catch {
        /* ignore teardown errors */
      }

      if (!ws || ws.readyState !== WebSocket.OPEN) {
        setState('stopped')
        cleanup()
        throw new Error(getServiceStrings().failedStopRecording)
      }

      setState('stopping')

      const resultPromise = new Promise<MeetingMinutesData>((resolve, reject) => {
        pendingMinutesRef.current = { resolve, reject, onProcessing }
      })

      const timeoutId = window.setTimeout(() => {
        const pending = pendingMinutesRef.current
        pendingMinutesRef.current = null
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ event: 'stop' }))
        }
        void waitForSocketClose(ws, STOP_FLUSH_TIMEOUT_MS)
        pending?.reject(new Error(getServiceStrings().meetingMinutesTimedOut))
      }, MEETING_MINUTES_TIMEOUT_MS)

      try {
        ws.send(JSON.stringify({ event: 'meeting-minutes' }))
        return await resultPromise
      } finally {
        window.clearTimeout(timeoutId)
      }
    },
    [cleanup, waitForSocketClose]
  )

  /**
   * Drops in-hook transcript buffers so a caller can archive them and then await
   * a picker without Output showing archived + still-active copies.
   */
  const clearTranscriptBuffers = useCallback(() => {
    setFinalTokens([])
    setPartialTokens([])
    setBatchTokens([])
    setLiveTurns([])
  }, [])

  /**
   * Puts the transcript so far on the room's clock — see the type above.
   *
   * Every buffer at once, because they are read side by side: Input and Output
   * are lined up on `start_ms`/`end_ms`, and Enhanced mode prints a turn's own
   * `startMs`–`endMs` range. Moving one and not the others would leave the
   * panes disagreeing about when the same sentence was said.
   *
   * @param shiftMs How much to add. Zero does nothing rather than replacing
   *   four arrays with equal copies of themselves.
   */
  const rebaseTranscriptClock = useCallback((shiftMs: number) => {
    if (shiftMs === 0) return
    setFinalTokens((current) => withShiftedTokenClock(current, shiftMs))
    setPartialTokens((current) => withShiftedTokenClock(current, shiftMs))
    setBatchTokens((current) => withShiftedTokenClock(current, shiftMs))
    setLiveTurns((current) => withShiftedTurnClock(current, shiftMs))
  }, [])

  /** Opens the STT proxy socket, sends config, and pipes captured audio chunks. */
  const start = useCallback(() => {
    if (wsRef.current) return

    const resumingCapture = resumeExistingCaptureRef.current

    setState('connecting')
    setError(null)
    pausedRef.current = false
    readyRef.current = false
    if (!resumingCapture) {
      setFinalTokens([])
      setPartialTokens([])
      setBatchTokens([])
      setLiveTurns([])
      setAssistCards([])
    }
    // Always drop in-flight ask state: the previous socket can no longer answer.
    resetAssistAskLifecycle()
    setMemoStatus('idle')
    setMemo(null)
    setMemoError(null)
    setContextUpdateStatus('idle')
    setContextCharCount(null)
    setContextUpdateError(null)
    setMainContentUpdateStatus('idle')
    setMainContentUpdateError(null)
    pendingMainContentRef.current = null
    setLanguageUpdateStatus('idle')
    setLanguageUpdateError(null)
    setProjectUpdateStatus('idle')
    setProjectUpdateError(null)
    setProjectUpdateErrorCode(null)
    clearAckTimer(projectUpdateTimerRef)
    projectUpdateRef.current = null
    setAppliedTargetLang(null)
    costSummaryRef.current = null

    void (async () => {
      try {
        // Asked for alongside the token, not after it. Both are round trips
        // this path already waits on, and sequencing them would put the
        // Backlog hop between the reader pressing record and being able to
        // speak. Never rejects, so it cannot be what stops a recording.
        const [{ apiKey, token, wsUrl }, backlogUserId] = await Promise.all([
          fetchSonioxToken(),
          ensureBacklogUserId(),
        ])
        // `expiresAt` (ISO datetime) is returned by the real proxy but not yet
        // consumed here; token TTL enforcement is out of scope for this hook.
        const sttToken = token ?? apiKey
        if (!sttToken || !wsUrl) {
          throw new Error(getServiceStrings().sttTokenInvalidResponse)
        }

        const ws = new WebSocket(buildTokenizedWsUrl(wsUrl, sttToken))
        ws.binaryType = 'arraybuffer'
        wsRef.current = ws

        ws.onmessage = (message) => {
          let event: ProxySttEvent
          try {
            event = JSON.parse(String(message.data)) as ProxySttEvent
          } catch {
            return
          }

          // Before any of the handling below, and walled off from it: a room
          // recording its own history must not be able to break the recording
          // it is listening to.
          try {
            onEventRef.current?.(event)
          } catch {
            // Deliberately ignored.
          }

          if (event.event === 'connected') {
            const activeSttProvider = sttProviderRef.current ?? 'soniox'
            const realtimeFeatures = getRealtimeFeaturesRef.current?.()
            const userName = getCurrentUserName()
            // What the call-matching detector can work with: it compares what
            // the tab is playing, so a microphone-only meeting is out of scope.
            const tabAudio =
              mixedSourcesRef.current === 'tab' || mixedSourcesRef.current === 'tab+mic'
            // Keep both realtime and batch event streams enabled for every
            // provider; providers may emit only the event kinds they support.
            const sharedRoomEnabled = Boolean(roomIdRef.current || tabAudio)
            ws.send(
              JSON.stringify({
                event: 'config',
                mode: 'both',
                sttProvider: toProxySttProvider(activeSttProvider),
                meetingId: meetingIdRef.current,
                // Named explicitly as well, so a room is visible on the wire
                // rather than hiding inside `meetingId`. Absent for a meeting
                // that shares nothing.
                ...(roomIdRef.current ? { roomId: roomIdRef.current } : {}),
                // A room of one is still a room. The server fixes a socket's
                // room when it connects, so a machine that might be joined
                // later has to already be sitting in one named after itself —
                // otherwise the reader whose machine is kept would have to
                // reconnect to admit anybody. Alone in it, it behaves exactly
                // as running alone did.
                //
                // Only for tab audio: a microphone-only meeting cannot be
                // recognised as the same call as anyone else's, so putting it
                // in a room would buy nothing.
                ...(sharedRoomEnabled
                  ? {
                      sharedRoom: true,
                      mixedSources: mixedSourcesRef.current,
                      // No output-device picker exists yet. `speaker` ranks
                      // below `headset`, so it is the safe default.
                      outputDevice: 'speaker',
                    }
                  : {}),
                // Sent even inside a room, where `meetingId` is the room's.
                ...(ownMeetingIdRef.current ? { ownMeetingId: ownMeetingIdRef.current } : {}),
                // The room's title is deliberately NOT here. BrSE reads it from
                // the database by `ownMeetingId` and sends it back as
                // `peerMeetingTitle`; a `meetingTitle` key on this frame is read
                // by nobody on the other side, so sending one only invites the
                // two names to disagree.
                // Only where an offer could be made at all, so a mic-only
                // meeting's frame is unchanged.
                ...(tabAudio
                  ? {
                      autoJoin:
                        pendingConnectAutoJoinRef.current ?? autoJoinRef.current,
                    }
                  : {}),
                userId: getCurrentUserId(),
                // The signed-in reader's name. Absent rather than blank, matching
                // the other optional keys on this frame.
                ...(userName ? { userName } : {}),
                // The same name again, under the key the AI service reads when
                // it builds `peerNames` for the shared-room offer — it does not
                // look at `userName`. Capped at the 100 characters the handoff
                // specifies, so a long display name cannot make the frame the
                // reason an offer never arrives.
                ...(userName ? { displayName: userName.slice(0, 100) } : {}),
                // The Backlog account behind the reader's grant, as the number
                // Backlog identifies it by — the field Backlog itself calls
                // `userId` is an opaque string and names nobody. Left out
                // entirely rather than sent as null when there is no grant, so
                // a reader who never connected Backlog sends the config that
                // was sent before this field existed.
                ...(backlogUserId !== null ? { userIdBacklog: backlogUserId } : {}),
                // Absent rather than null for a meeting with no project, like
                // every other optional key on this frame.
                ...(projectKeyRef.current ? { projectKey: projectKeyRef.current } : {}),
                // Absent for a shared room, and absent on a build that has
                // never been told — the server's own default answers both the
                // same way, so an absent key is never a different meeting.
                ...(assistModelRef.current ? { assistModel: assistModelRef.current } : {}),
                // Absent in a shared room, and absent on a build that was
                // never told — the contract reads an absent list as both
                // routes, which is what this panel starts on anyway.
                ...(assistRoutesRef.current ? { assistRoutes: assistRoutesRef.current } : {}),
                meetingElapsedMs: getMeetingElapsedMsRef.current?.(),
                // Omitted for an ordinary meeting, which is the server default —
                // see `meetingModeForSessionKind`.
                ...(meetingModeRef.current ? { meetingMode: meetingModeRef.current } : {}),
                // Absent when this screen has no switches. A present map is the
                // full four keys — a stray name here closes the socket.
                ...(realtimeFeatures ? { features: realtimeFeatures } : {}),
                audioFormat: 'pcm_s16le',
                sampleRate: 16000,
                numChannels: 1,
                translation: {
                  type: modeRef.current ?? 'one_way',
                  sourceLang: sourceLangRef.current,
                  targetLang: targetLangRef.current,
                },
                context: normalizeSttConfigContext(contextRef.current),
              })
            )
            pendingConnectAutoJoinRef.current = null
            const pendingRejoin = pendingSharedRoomRejoinRef.current
            if (pendingRejoin) {
              pendingSharedRoomRejoinRef.current = null
              ws.send(JSON.stringify({ event: 'sharedroom.rejoin', roomId: pendingRejoin }))
            }
            return
          }

          if (event.event === 'ready') {
            const sentMode = meetingModeRef.current
            const verdict = verifyReadyMeetingMode(sentMode, event.meetingMode)
            if (isFatalReadyModeVerdict(verdict, sentMode)) {
              // The server is running a different session kind than the one
              // asked for. Stop before any audio goes out — the config schema
              // ignores fields it does not know, so this echo is the only place
              // the disagreement is visible at all.
              setError(new Error(getServiceStrings().meetingModeMismatch))
              setState('error')
              ws.close()
              return
            }

            readyRef.current = true
            const seedMs = event.timeline_seed_ms
            if (typeof seedMs === 'number') {
              onTimelineAnchorRef.current?.({ seedMs, resumed: event.timeline_resumed === true })
            }
            setState('recording')
            if (pauseOnReconnectRef.current) {
              // Back up paused, the way we went down. The server is told too:
              // it is a different socket and knows nothing of the pause the
              // previous one was in.
              pauseOnReconnectRef.current = false
              pausedRef.current = true
              ws.send(JSON.stringify({ event: 'pause' }))
              setState('paused')
            }
            setConfirmedSttProvider(event.sttProvider ?? null)
            onRealtimeFeaturesReadyRef.current?.(event)
            const handlers: AudioSourceHandlers = {
                onData: (chunk) => {
                  // Paused meetings must send no audio at all — Soniox bills
                  // silent PCM exactly like speech, so this gate is what
                  // actually stops the cost, not just the mic mute below.
                  if (pausedRef.current) return
                  if (ws.readyState === WebSocket.OPEN) {
                    ws.send(chunk)
                  }
                },
                onError: (err) => {
                  setError(err)
                  // Capture ending is not the same as the session failing.
                  // Enhanced mode runs well behind the speech, so the last turns
                  // are usually transcribed but not yet translated at this
                  // point; closing here made the server read `client_disconnect`
                  // and skip its final flush, and everything still in flight was
                  // delivered to a peer that had gone. `drain` asks for that
                  // flush while leaving the socket open to receive it, and —
                  // unlike `stop` — leaves the timeline anchor in place so a
                  // later Start continues the meeting clock instead of restarting
                  // it at 00:00. The server acknowledges nothing: it closes the
                  // socket when it has finished, so `onclose` is the signal.
                  if (isCaptureLostError(err) && ws.readyState === WebSocket.OPEN) {
                    setState('draining')
                    ws.send(JSON.stringify({ event: 'drain' }))
                    clearDrainTimer()
                    drainTimerRef.current = setTimeout(() => {
                      drainTimerRef.current = null
                      ws.close()
                    }, DRAIN_TIMEOUT_MS)
                    return
                  }
                  setState('error')
                  ws.close()
                },
            }

            // Re-tap, never re-start: the capture — and the MediaRecorder
            // archiving this meeting's audio — is still running from before the
            // outage. Calling `start` again would build a second AudioContext
            // over the same devices and hand the archive a brand new stream,
            // which is exactly what threw away the pre-outage audio (BRAI-366).
            if (resumingCapture) {
              resumeExistingCaptureRef.current = false
              const retappable = sourceRef.current as AudioSource & {
                restart?: (handlers: AudioSourceHandlers) => void
              }
              if (retappable.restart) {
                retappable.restart(handlers)
                return
              }
            }

            void sourceRef.current.start(handlers).catch((err: unknown) => {
              setError(
                err instanceof Error ? err : new Error(getServiceStrings().failedStartAudioCapture)
              )
              setState('error')
              ws.close()
            })
            return
          }

          if (event.event === 'error') {
            setError(new Error(event.message ?? event.event))
            setState('error')
            return
          }

          if (event.event === 'image.processed') {
            return
          }

          // Per-batch translation failures are non-fatal: the session stays
          // open and recording continues, so `state` is intentionally left
          // untouched here (unlike the fatal top-level `error` event above).
          if (event.event === 'translation.warning') {
            return
          }

          // Live-summary push events. The server re-analyzes the whole transcript on a
          // fixed cadence and now pushes the result as three frames, one per pane, so the memo
          // is assembled across frames instead of replaced by each one — `applyMemoPane` owns
          // that and the reasoning behind it. Non-fatal either way: the STT session keeps
          // running regardless of the summarize outcome.
          if (event.event === 'summarize.processing') {
            setMemoStatus('loading')
            setMemoError(null)
            return
          }

          if (isMemoPaneEvent(event.event)) {
            setMemo((previous) => applyMemoPane(previous, event))
            setMemoStatus('success')
            setMemoError(null)
            return
          }

          if (event.event === 'summarize.error') {
            setMemoError(event.message ?? event.event)
            setMemoStatus('error')
            return
          }

          if (event.event === 'assist.card') {
            setAssistAskError(null)
            const card = {
              ...(event as unknown as AssistCard),
              elapsedMs: getMeetingElapsedMsRef.current?.() ?? 0,
            }
            if (card.type === 'answer') {
              if (assistAskTimeoutRef.current) {
                clearTimeout(assistAskTimeoutRef.current)
                assistAskTimeoutRef.current = null
              }
              setAssistAskPending(false)
              setAssistCards((current) => upsertAssistCard(current, card))
            } else {
              setAssistCards((current) => [...current, card])
            }
            return
          }

          if (event.event === 'features.updated') {
            onRealtimeFeaturesUpdatedRef.current?.(event)
            return
          }

          if (event.event === 'features.update.error') {
            onRealtimeFeaturesErrorRef.current?.(
              event.code === 'not_supported' ? 'not_supported' : 'bad_request'
            )
            return
          }

          if (event.event === 'assist.routes.status') {
            onAssistRoutesStatusRef.current?.(event)
            return
          }

          if (event.event === 'assist.routes.update.error') {
            onAssistRoutesErrorRef.current?.(
              event.code === 'not_supported' ? 'not_supported' : 'bad_request'
            )
            return
          }

          if (event.event === 'assist.model.status') {
            onAssistModelStatusRef.current?.(event)
            return
          }

          if (event.event === 'assist.model.update.error') {
            onAssistModelErrorRef.current?.(
              event.code === 'not_supported' ? 'not_supported' : 'bad_request'
            )
            return
          }

          if (event.event === 'assist.ask.error') {
            if (assistAskTimeoutRef.current) {
              clearTimeout(assistAskTimeoutRef.current)
              assistAskTimeoutRef.current = null
            }
            setAssistAskPending(false)
            setAssistAskError({
              code: event.code ?? 'bad_request',
              message: event.message ?? '',
              // Carried rather than rebuilt around: this object is assembled
              // field by field, so a field nobody names here is dropped, and
              // `reason` is the only thing that tells six Backlog refusals
              // apart.
              ...(event.reason ? { reason: event.reason } : {}),
            })
            return
          }

          // Acknowledgment for a client-sent `context.update` (see
          // `sendContextUpdate`). Non-fatal either way — the STT session keeps
          // running regardless of whether the context was accepted.
          if (event.event === 'context.updated') {
            setContextCharCount(event.realtime_context_chars ?? null)
            setContextUpdateStatus('success')
            setContextUpdateError(null)
            return
          }

          if (event.event === 'context.update.error') {
            setContextUpdateError(event.message ?? event.event)
            setContextUpdateStatus('error')
            return
          }

          // The edited narrative is now the server's source of truth for the
          // next summarize cycle, which will preserve it verbatim and append
          // only newly transcribed content. Apply it to `memo` now too, since
          // otherwise the UI would keep showing the pre-edit text until the
          // next periodic `summarize.result` arrives.
          if (event.event === 'main-content.updated') {
            setMainContentUpdateStatus('success')
            setMainContentUpdateError(null)
            const pending = pendingMainContentRef.current
            if (pending) {
              setMemo((current) => (current ? { ...current, main_content: pending } : current))
              pendingMainContentRef.current = null
            }
            return
          }

          // Rejection is non-fatal: transcript and summary streaming continue
          // while the editor keeps the user's draft available for retry.
          if (event.event === 'main-content.update.error') {
            setMainContentUpdateError(event.message ?? event.event)
            setMainContentUpdateStatus('error')
            pendingMainContentRef.current = null
            return
          }

          // A live language update only rotates the upstream STT session; this
          // socket and all accumulated client/server state remain active.
          if (event.event === 'language.updated') {
            const acknowledgment = event as unknown as LanguageUpdatedEvent
            setAppliedTargetLang(acknowledgment.translation?.targetLang ?? null)
            setLanguageUpdateStatus('success')
            setLanguageUpdateError(null)
            return
          }

          // Rejection is non-fatal: transcript streaming continues on the
          // previously applied translation language.
          if (event.event === 'language.update.error') {
            setLanguageUpdateError(event.message ?? event.event)
            setLanguageUpdateStatus('error')
            return
          }

          // A project or a freshly connected Backlog account, applied to the
          // session that is already running (BRAI-689). Nothing restarts: the
          // transcript, the running summary and the accrued cost carry on, and
          // only the next batch picks up the new context.
          if (event.event === 'project.updated') {
            const pending = projectUpdateRef.current
            // Matched against what was asked for rather than counted. A second
            // change started before the first is acknowledged cancels it
            // server-side, so the only reply that ever arrives belongs to the
            // last one — and settling the wrong request would report a change
            // that never took.
            if (!pending) return
            if (!acknowledgesProjectUpdate(pending, event as unknown as ProjectUpdatedEvent)) return
            clearAckTimer(projectUpdateTimerRef)
            projectUpdateRef.current = null
            setProjectUpdateStatus('success')
            setProjectUpdateError(null)
            setProjectUpdateErrorCode(null)
            return
          }

          // Non-fatal, like every other refusal on this socket: the meeting
          // keeps running on whatever project it already had.
          if (event.event === 'project.update.error') {
            clearAckTimer(projectUpdateTimerRef)
            projectUpdateRef.current = null
            setProjectUpdateError(event.message ?? event.event)
            setProjectUpdateErrorCode(typeof event.code === 'string' ? event.code : null)
            setProjectUpdateStatus('error')
            return
          }

          // Pushed once, at meeting end, with the total accrued STT/translation
          // cost for the session. Non-fatal — the STT session keeps running
          // regardless of whether this arrives.
          if (event.event === 'cost.summary') {
            if (event.currency && typeof event.total_cost_usd === 'number') {
              costSummaryRef.current = {
                currency: event.currency,
                totalCostUsd: event.total_cost_usd,
              }
            }
            return
          }

          // On-demand end-of-meeting minutes, requested by `finalizeMeetingMinutes`.
          // Only one request is ever in flight per session (tracked via
          // `pendingMinutesRef`), so these branches are no-ops when nothing
          // is pending (e.g. a stray/duplicate server push).
          if (event.event === 'meeting-minutes.processing') {
            pendingMinutesRef.current?.onProcessing?.()
            return
          }

          if (event.event === 'meeting-minutes.result') {
            const pending = pendingMinutesRef.current
            pendingMinutesRef.current = null
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ event: 'stop' }))
            }
            if (event.data) {
              pending?.resolve(event.data as MeetingMinutesData)
            } else {
              pending?.reject(new Error(getServiceStrings().failedGenerateMeetingMinutes))
            }
            return
          }

          if (event.event === 'meeting-minutes.error') {
            const pending = pendingMinutesRef.current
            pendingMinutesRef.current = null
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ event: 'stop' }))
            }
            pending?.reject(new Error(event.message ?? event.event))
            return
          }

          // Formerly dropped every event while paused (BRAI-209), back when
          // pause only muted the mic and kept streaming silent PCM — Soniox
          // could still produce (garbage) results from that silence. Now that
          // `pause` stops sending audio entirely, the backend finalizes the
          // STT session on `pause` and the resulting `*.final` for whatever
          // was said right before pausing arrives while still in the
          // `paused` state; dropping it here would silently lose that last
          // sentence from the transcript, saved recording, and minutes.
          setLiveTurns((current) => applyLiveTranscriptEvent(current, event))

          const tokens = proxyEventToTokens(event)

          // Partial state is scoped per `translation_status`: `google` mode
          // streams an 'original' partial (from `transcript.partial`) and a
          // 'translation' partial (from `translation.partial`) concurrently
          // off the same audio, so clearing/replacing one status's interim
          // draft must never clobber the other status's live draft.
          if (tokens.clearPartial || tokens.partialTokens.length > 0) {
            const status = tokens.partialStatus ?? 'original'
            setPartialTokens((current) => {
              const otherStatusTokens = current.filter(
                (token) => token.translation_status !== status
              )
              return tokens.partialTokens.length > 0
                ? [...otherStatusTokens, ...tokens.partialTokens]
                : otherStatusTokens
            })
          }
          if (tokens.finalTokens.length > 0) {
            setFinalTokens((current) => [...current, ...tokens.finalTokens])
          }
          if (tokens.batchTokens.length > 0) {
            setBatchTokens((current) => [...current, ...tokens.batchTokens])
          }
        }

        ws.onclose = (event) => {
          const pendingMinutes = pendingMinutesRef.current
          pendingMinutesRef.current = null
          cleanup()
          pendingMinutes?.reject(new Error(getServiceStrings().failedGenerateMeetingMinutes))
          setState((current) => {
            if (current === 'error' || current === 'canceled') return current
            // A drain ends by the server closing the socket, whatever code it
            // uses — the session finished on purpose, so it is not an error.
            if (current === 'draining') return 'stopped'
            const nextState = event.code === 1000 || current === 'stopping' ? 'stopped' : 'error'
            if (nextState === 'error') {
              const closeMessage = getCloseCodeErrorMessage(event.code)
              if (closeMessage) setError(new Error(closeMessage))
            }
            return nextState
          })
        }

        ws.onerror = () => {
          setError(new Error(getServiceStrings().websocketError))
          setState('error')
        }

        await waitForSocketOpen(ws)
      } catch (err) {
        cleanup()
        setError(err instanceof Error ? err : new Error(getServiceStrings().failedStartRecording))
        setState('error')
      }
    })()
  }, [cleanup, clearDrainTimer, resetAssistAskLifecycle])

  /**
   * Reopens the socket for a session that was interrupted by network loss,
   * continuing the same capture, transcript and archive recording.
   *
   * Only valid from `'interrupted'`: any other state either still owns a live
   * socket or has already torn the capture down, and re-tapping a capture that
   * no longer exists would leave a session that looks live and records nothing.
   */
  const reconnect = useCallback(() => {
    if (state !== 'interrupted') return
    resumeExistingCaptureRef.current = true
    sourceRef.current.resume?.()
    start()
  }, [start, state])

  /**
   * Answers the server's offer to merge this machine into a shared room.
   *
   * There is no timeout on the offer, by the AI service's own contract: it
   * waits until every machine has answered or one of them leaves. So `false`
   * here is not a decline — it means the socket closed under the offer, and
   * the caller should drop the prompt rather than retry.
   *
   * @param matchId The offer being answered, as the server named it.
   * @param accept  Both answers are sent; silence is not an answer.
   * @returns False when there is no open socket to answer on.
   */
  const sendSharedRoomReply = useCallback((matchId: string, accept: boolean): boolean => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return false
    ws.send(JSON.stringify({ event: 'sharedroom.match_reply', matchId, accept }))
    return true
  }, [])

  const clearPendingSharedRoomRejoin = useCallback(() => {
    pendingSharedRoomRejoinRef.current = null
  }, [])

  const sendSharedRoomRejoin = useCallback(
    (roomId: string): boolean => {
      const trimmed = roomId.trim()
      if (!trimmed) return false
      const ws = wsRef.current
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ event: 'sharedroom.rejoin', roomId: trimmed }))
        return true
      }
      if (state !== 'recording' && state !== 'paused') return false
      pendingSharedRoomRejoinRef.current = trimmed
      return true
    },
    [state]
  )

  /**
   * Moves a live session onto another room without interrupting the recording.
   *
   * The socket is dropped rather than stopped. `stop` tells the AI service the
   * reader's own meeting has ended, and it would post the duration webhook on
   * the spot — for a meeting that is still being recorded.
   *
   * Built from the two pieces the offline path already proved: the drop keeps
   * the AudioContext and the archive's `MediaRecorder` alive, and
   * `resumeExistingCaptureRef` keeps the transcript, so the lines said before
   * the merge are still there afterwards. The id is written to the ref rather
   * than waited for through props, because the caller is inside an event
   * handler and the store's own update has not re-rendered yet.
   *
   * @param roomId The room to continue in; becomes this socket's `meetingId`.
   * @returns False when there was no session to move.
   */
  const rejoinSharedRoom = useCallback(
    (roomId: string, options?: { autoJoin?: boolean }): boolean => {
      // A socket is not the same as a live session: `stop()` holds `wsRef`
      // while it waits for the flush, so an offer landing in that window used
      // to open a fresh socket for a meeting the reader had just ended.
      if (state !== 'recording' && state !== 'paused') return false
      if (!wsRef.current) return false
      if (options?.autoJoin !== undefined) {
        pendingConnectAutoJoinRef.current = options.autoJoin
      }
      rejoinedRoomIdRef.current = roomId
      meetingIdRef.current = roomId
      pauseOnReconnectRef.current = state === 'paused'
      dropSocketForOffline()
      resumeExistingCaptureRef.current = true
      // `dropSocketForOffline` pauses the source on its way out, so this is
      // what resumes it — and a reader who was already paused is left alone.
      if (state !== 'paused') sourceRef.current.resume?.()
      start()
      return true
    },
    [dropSocketForOffline, start, state]
  )

  /**
   * Pauses capture AND stops feeding the socket, so the paused span costs
   * nothing (Soniox bills processed audio regardless of silence). Gates
   * `onData` before telling the source/server so no chunk sent after this
   * call can slip through.
   */
  const pause = useCallback(() => {
    if (state !== 'recording') return
    pausedRef.current = true
    sourceRef.current.pause?.()
    const ws = wsRef.current
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ event: 'pause' }))
    }
    setState('paused')
  }, [state])

  /** Resumes audio capture after a local pause, telling the server first. */
  const resume = useCallback(() => {
    if (state !== 'paused') return
    const ws = wsRef.current
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ event: 'resume' }))
    }
    sourceRef.current.resume?.()
    pausedRef.current = false
    setState('recording')
  }, [state])

  /**
   * Replaces the reference context used by the next live-summary
   * (`summarize.*`) pass. Sends an empty string to clear it. No-op if the
   * socket isn't open; the server acknowledges with `context.updated` or a
   * non-fatal `context.update.error`.
   *
   * Sets status to `pending` before send so a later `context.updated` always
   * transitions into `success` — even when `realtime_context_chars` matches
   * the previous save (same length, different text). Without that transition,
   * MeetingChat's close-on-success effect would not re-run.
   *
   * @param context Free-text reference context (e.g. "Đang xem slide 3…"); empty/whitespace clears it.
   */
  const sendContextUpdate = useCallback((context: string) => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    setContextUpdateStatus('pending')
    setContextUpdateError(null)
    ws.send(JSON.stringify({ event: 'context.update', realtime_context: context }))
  }, [])

  /**
   * Replaces the server-owned live Summary with the full paragraph array that
   * the user currently sees. Full replacement avoids relying on paragraph
   * indices while the server may append new content concurrently.
   *
   * @param paragraphs Complete edited `main_content` narrative.
   * @returns `false` when no open socket exists and nothing was sent.
   */
  const sendMainContentUpdate = useCallback((paragraphs: string[]): boolean => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return false

    setMainContentUpdateStatus('pending')
    setMainContentUpdateError(null)
    pendingMainContentRef.current = paragraphs
    ws.send(JSON.stringify({ event: 'main-content.update', main_content: paragraphs }))
    return true
  }, [])

  /**
   * Switches translation language on the active socket without reconnecting.
   *
   * @param nextTargetLang Language code requested for subsequent translations.
   * @returns `false` when no open socket exists and nothing was sent.
   */
  const sendLanguageUpdate = useCallback((nextTargetLang: string): boolean => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return false

    setLanguageUpdateStatus('pending')
    setLanguageUpdateError(null)
    ws.send(
      JSON.stringify({
        event: 'language.update',
        translation: {
          type: modeRef.current ?? 'one_way',
          sourceLang: sourceLangRef.current,
          targetLang: nextTargetLang,
        },
      })
    )
    return true
  }, [])

  /**
   * Applies a project, or a newly connected Backlog account, to the running
   * session without reconnecting (BRAI-689).
   *
   * Everything the meeting has accumulated survives — transcript, running
   * summary, speaker map, accrued cost — and the new context reaches
   * translation, the minutes and the Copilot from the next batch onwards.
   *
   * The caller must have written the project to the meeting row first. The
   * server reads that row for on-demand minutes and for post-meeting learning,
   * and a row still naming the old project drops the minutes' title and date.
   *
   * @param request The keys to change; a key left out keeps its current value.
   * @returns `false` when there is no open socket, or when the request names
   *   nothing to change — in both cases nothing was sent.
   */
  const sendProjectUpdate = useCallback((request: ProjectUpdateRequest): boolean => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return false
    if (!isSendableProjectUpdate(request)) return false

    clearAckTimer(projectUpdateTimerRef)
    projectUpdateRef.current = request
    setProjectUpdateStatus('pending')
    setProjectUpdateError(null)
    setProjectUpdateErrorCode(null)
    ws.send(JSON.stringify({ event: 'project.update', ...request }))

    // Silence is the only thing a server that predates this frame says about
    // it, so silence has to be timed.
    //
    // The identity check below looks unreachable, because every path that
    // changes the pending request also cancels this clock. It guards the one
    // that cannot: a timer that has already elapsed is queued as a task, and
    // `clearTimeout` does not un-queue it — so an acknowledgment arriving in
    // that same turn settles the request, and this callback still runs
    // afterwards. Without the check it would then overwrite a success with
    // `unsupported`. Not reachable through fake timers, which run the callback
    // synchronously; do not delete it for being untested.
    projectUpdateTimerRef.current = window.setTimeout(() => {
      projectUpdateTimerRef.current = null
      if (projectUpdateRef.current !== request) return
      projectUpdateRef.current = null
      setProjectUpdateStatus('unsupported')
    }, PROJECT_UPDATE_ACK_TIMEOUT_MS)
    return true
  }, [])

  // The clock outlives this component otherwise: leaving the meeting with an
  // update in flight would fire a `setState` into an unmounted tree.
  useEffect(() => () => clearAckTimer(projectUpdateTimerRef), [])

  /**
   * Sends a Copilot assist question on the live STT socket.
   *
   * @returns `false` when the session is not open or not yet `ready`, so the
   *   caller can keep the input and surface `assistAskError`.
   */
  const sendAssistAsk = useCallback((input: AssistAskInput): boolean => {
    const ws = wsRef.current
    if (!isLiveSttReady(ws, readyRef.current)) {
      setAssistAskError({ code: 'session_not_ready', message: '' })
      setAssistAskPending(false)
      return false
    }

    setAssistAskError(null)
    setAssistAskPending(true)
    if (assistAskTimeoutRef.current) clearTimeout(assistAskTimeoutRef.current)
    assistAskTimeoutRef.current = setTimeout(() => {
      setAssistAskPending(false)
    }, ASSIST_ASK_TIMEOUT_MS)

    ws.send(JSON.stringify({ event: 'assist.ask', ...input }))
    return true
  }, [])

  /**
   * Ask this socket to stop or resume one or more realtime panes.
   *
   * @param features Flags to change. Only boolean keys of {@link RealtimeFeatures} are sent.
   * @returns False when the session is not ready or the partial is empty.
   */
  const sendFeaturesUpdate = useCallback((features: Partial<RealtimeFeatures>): boolean => {
    const ws = wsRef.current
    if (!isLiveSttReady(ws, readyRef.current)) return false
    const partial = sanitizeFeatureUpdate(features)
    if (!partial) return false
    ws.send(JSON.stringify({ event: 'features.update', features: partial }))
    return true
  }, [])

  /**
   * Asks for a different assistant for the rest of the meeting.
   *
   * The answer is `assist.model.status`, not this return value: true only
   * means the frame went out.
   *
   * @param model The assistant to switch to.
   * @returns False when there is no live ready session to send on.
   */
  const sendAssistRoutesUpdate = useCallback((routes: AssistRoute[]): boolean => {
    const ws = wsRef.current
    if (!isLiveSttReady(ws, readyRef.current)) return false
    ws.send(JSON.stringify({ event: 'assist.routes.update', routes }))
    return true
  }, [])

  /**
   * Asks for a different set of routes for the rest of the meeting.
   *
   * The answer is `assist.routes.status`, not this return value: true only
   * means the frame went out.
   *
   * @param model The assistant to switch to.
   * @returns False when there is no live ready session to send on.
   */
  const sendAssistModelUpdate = useCallback((model: AssistModel): boolean => {
    const ws = wsRef.current
    if (!isLiveSttReady(ws, readyRef.current)) return false
    ws.send(JSON.stringify({ event: 'assist.model.update', model }))
    return true
  }, [])

  /**
   * Asks the service to translate speech so far in hard/precise mode.
   *
   * @returns `false` when there is no live ready session to send on.
   */
  const sendTranslationHard = useCallback((): boolean => {
    const ws = wsRef.current
    if (!isLiveSttReady(ws, readyRef.current)) return false

    ws.send(JSON.stringify({ event: 'translation-hard' }))
    return true
  }, [])

  /**
   * Forwards a captured JPEG as `{ event: "image", image }` on the STT socket.
   * Text-only so PCM binary traffic is never corrupted. No-op until `ready`,
   * while paused, or when the socket is not open; the capture loop retries later.
   */
  const sendScreenFrame = useCallback((message: ScreenFrameMessage): boolean => {
    const ws = wsRef.current
    if (!isLiveSttReady(ws, readyRef.current)) return false
    if (pausedRef.current) return false

    ws.send(JSON.stringify({ event: 'image', image: message.image }))
    return true
  }, [])

  return useMemo(
    () => ({
      state,
      isActive: state === 'connecting' || state === 'recording' || state === 'paused',
      isRecording: state === 'recording',
      isPaused: state === 'paused',
      error,
      confirmedSttProvider,
      finalTokens,
      partialTokens,
      batchTokens,
      liveTurns,
      memoStatus,
      memo,
      memoError,
      assistCards,
      assistAskError,
      assistAskPending,
      contextUpdateStatus,
      contextCharCount,
      contextUpdateError,
      mainContentUpdateStatus,
      mainContentUpdateError,
      languageUpdateStatus,
      languageUpdateError,
      projectUpdateStatus,
      projectUpdateError,
      projectUpdateErrorCode,
      appliedTargetLang,
      costSummaryRef,
      start,
      stop,
      finalizeMeetingMinutes,
      cancel,
      clearTranscriptBuffers,
      rebaseTranscriptClock,
      dropSocketForOffline,
      reconnect,
      sendSharedRoomReply,
      sendSharedRoomRejoin,
      clearPendingSharedRoomRejoin,
      rejoinSharedRoom,
      pause,
      resume,
      sendContextUpdate,
      sendMainContentUpdate,
      sendLanguageUpdate,
      sendProjectUpdate,
      sendScreenFrame,
      sendTranslationHard,
      sendAssistAsk,
      sendFeaturesUpdate,
      sendAssistModelUpdate,
      sendAssistRoutesUpdate,
    }),
    [
      appliedTargetLang,
      assistCards,
      assistAskError,
      assistAskPending,
      batchTokens,
      cancel,
      clearPendingSharedRoomRejoin,
      clearTranscriptBuffers,
      rebaseTranscriptClock,
      dropSocketForOffline,
      reconnect,
      sendSharedRoomReply,
      sendSharedRoomRejoin,
      rejoinSharedRoom,
      contextCharCount,
      contextUpdateError,
      contextUpdateStatus,
      costSummaryRef,
      error,
      finalizeMeetingMinutes,
      finalTokens,
      liveTurns,
      languageUpdateError,
      languageUpdateStatus,
      projectUpdateError,
      projectUpdateErrorCode,
      projectUpdateStatus,
      mainContentUpdateError,
      mainContentUpdateStatus,
      memo,
      memoError,
      memoStatus,
      partialTokens,
      pause,
      resume,
      sendContextUpdate,
      sendMainContentUpdate,
      sendLanguageUpdate,
      sendProjectUpdate,
      sendScreenFrame,
      sendTranslationHard,
      sendAssistAsk,
      sendFeaturesUpdate,
      sendAssistModelUpdate,
      sendAssistRoutesUpdate,
      start,
      state,
      stop,
      confirmedSttProvider,
    ]
  )
}

/**
 * The same lines with every time they carry moved on by `shiftMs`.
 *
 * `start_ms` is the only one of those: the tokens this hook holds come from
 * its own `proxyEventToTokens`, which reads `start_time` and nothing else.
 * When a line ends is carried by the turn beside it — see
 * {@link withShiftedTurnClock}.
 *
 * A token with no `start_ms` is left exactly as it is rather than given one:
 * an absent time means the service never said when, and inventing an origin
 * for it would put it somewhere definite on a timeline it was never on.
 *
 * @param tokens  Whatever the hook is holding.
 * @param shiftMs How much later the new clock reads than the old one.
 * @returns The same array when there is nothing in it, so an idle buffer does
 *   not cause a render.
 */
function withShiftedTokenClock(tokens: RealtimeToken[], shiftMs: number): RealtimeToken[] {
  if (tokens.length === 0) return tokens
  return tokens.map((token) =>
    typeof token.start_ms === 'number' ? { ...token, start_ms: token.start_ms + shiftMs } : token
  )
}

/** {@link withShiftedTokenClock} for the turns Enhanced mode prints a range from. */
function withShiftedTurnClock(
  turns: LiveTranscriptTurn[],
  shiftMs: number
): LiveTranscriptTurn[] {
  if (turns.length === 0) return turns
  return turns.map((turn) => ({
    ...turn,
    ...(typeof turn.startMs === 'number' ? { startMs: turn.startMs + shiftMs } : {}),
    ...(typeof turn.endMs === 'number' ? { endMs: turn.endMs + shiftMs } : {}),
  }))
}
